import { getUserContainer } from 'container/user-module'
import { ILoggedUserService } from 'modules/users/services/LoggedUserService'
import { IUserService, LOGGED_USER_SERVICE_KEY, USER_SERVICE_KEY } from 'modules/users'
import { emptyUserDTO, UserDTO } from 'modules/users/models/User'
import React, { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppButton, ButtonTheme } from 'components/app-button/AppButton'
import styles from './ProfileTab.module.scss'
import genericStyle from '../../../../common/utils/generic.module.css'
import { ROUTE_NEWS_WALL } from 'routes/routes-constants'
import { Header } from 'components/header/Header'
import { Box, Button, Typography, Alert, Skeleton, MenuItem, Tooltip } from '@mui/material'
import { UpdatePasswordDTO } from 'modules/users/models/UpdatePasswordDTO'
import { IStatusService } from '../../../../common/status/StatusService'
import { getAppContainer, STATUS_SERVICE_KEY } from 'container/app'
import AvatarProfile from 'components/avatar-profile/AvatarProfile'
import { Input } from 'pages/user-profile/userProfileStylesMui'
import { useIsOpenMenuLeftContext } from '../../../../common/utils/isOpenMenuLeftContext'
import { useNavigate } from 'react-router-dom'
import { UserGender } from 'modules/users/enums/UserGender'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { CalendarIcon } from 'features/calendar/Calendar'
import { reduceString } from 'common/utils/strings'

const userContainer = getUserContainer()
const userService = userContainer.get<IUserService>(USER_SERVICE_KEY)
const loggedUserService = userContainer.get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)
const statusService = getAppContainer().get<IStatusService>(STATUS_SERVICE_KEY)

export function ProfileTab() {
  const { t } = useTranslation()
  const loggedUser = loggedUserService.get()
  const [userToEdit, setUserToEdit] = useState<UserDTO>(emptyUserDTO())
  const [isDatePickerOpen, setIsDatePickerOpen] = useState<boolean>(false)
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [repeatPassword, setRepeatPassword] = useState('')
  const [message, setMessage] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { openMenuLeft } = useIsOpenMenuLeftContext()
  const navigate = useNavigate()

  useEffect(() => {
    userService.getByID(loggedUser?.id || '').subscribe((res) => {
      res && setUserToEdit(res.toDTO())
      setIsLoading(false)
    })
  }, [])

  useEffect(() => {
    setMessage('')
  }, [userToEdit])

  const isPatient = useMemo(() => {
    return userToEdit?.roles.includes('patient')
  }, [userToEdit])

  const handleInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setUserToEdit(Object.assign({ ...userToEdit }, { [e.target.name]: e.target.value }))

  const goHome = () => navigate(ROUTE_NEWS_WALL)

  const validatePassword = () => {
    if (!newPassword && !repeatPassword) {
      setErrorMessage(t('voidFields'))
      return false
    }

    if (newPassword !== repeatPassword) {
      setErrorMessage('passNotEquals')
      return false
    }

    return true
  }

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    setUserToEdit({
      ...userToEdit,
      dob: date ? date.toDate() : new Date(),
    })
  }

  const saveData = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (oldPassword) {
      if (!validatePassword() || !loggedUser) {
        setErrorMessage('incorrectOldPass')
        return
      }

      setErrorMessage('')
      const newPasswordSend: UpdatePasswordDTO = {
        id: loggedUser.id,
        password: oldPassword,
        newPassword: newPassword,
      }

      userService.changePassword(newPasswordSend).subscribe({
        next: (res: any) => {
          if (res === 'incorrectPassword') {
            setErrorMessage('incorrectOldPass')
          } else {
            updateUserProfile()
          }
        },
        error: (err) => {
          setErrorMessage('anErrorOccured')
        },
      })
    } else {
      updateUserProfile()
    }
  }

  const updateUserProfile = () => {
    userService.update(userToEdit).subscribe({
      next: () => statusService.sendStatus({ variant: 'success' }),
      error: () => setErrorMessage('error'),
    })
  }

  return (
    <Box className={`${genericStyle.container} ${styles.parentContainer}`}>
      {!isLoading ? (
        <form className={styles.form} onSubmit={saveData}>
          <Box className={styles.formDataContainer}>
            <Box className={styles.avatarContainer}>
              <AvatarProfile />
              <Tooltip title={`${userToEdit.firstName} ${userToEdit.lastName}`}>
                <Box className={styles.nameContainer}>
                  <Typography className={styles.userName}>
                    {reduceString(`${userToEdit.firstName} ${userToEdit.lastName}`, 80)}
                  </Typography>
                  <Typography className={styles.roleText}>{t('externProfessional')}</Typography>
                </Box>
              </Tooltip>
            </Box>
            <Box mt={4} width={'100%'}>
              <Box
                style={{ maxWidth: openMenuLeft ? '100%' : '77%' }}
                className={styles.headerContainer}
              >
                <Typography className={styles.headerTitle}>{t('userData')}</Typography>
              </Box>
              <Box mb={3} className={styles.textFieldBox}>
                <Box>
                  <Typography className={styles.inputTitle}>{t('name')}</Typography>
                  <Input
                    id={'firstName'}
                    label=""
                    key={'firstName'}
                    name="firstName"
                    className={styles.textField}
                    value={userToEdit.firstName}
                    onChange={handleInput}
                    type="text"
                    required={true}
                    variant={'outlined'}
                    size={'small'}
                    disabled={isPatient}
                  />
                </Box>
                <Box>
                  <Typography className={styles.inputTitle}>{t('lastName')}</Typography>
                  <Input
                    id={'lastName'}
                    label={''}
                    key={'lastName'}
                    name="lastName"
                    className={styles.textField}
                    value={userToEdit.lastName}
                    onChange={handleInput}
                    type="text"
                    required={true}
                    variant={'outlined'}
                    size={'small'}
                    disabled={isPatient}
                  />
                </Box>
              </Box>
              <Box mb={3} className={styles.textFieldBox}>
                <Box>
                  <Typography className={styles.inputTitle}>{t('email')}</Typography>
                  <Input
                    id={'email'}
                    label={''}
                    key={'email'}
                    name="email"
                    className={styles.textField}
                    value={userToEdit.email}
                    type="email"
                    onChange={handleInput}
                    required={true}
                    variant={'outlined'}
                    size={'small'}
                    disabled={true}
                  />
                </Box>
                <Box>
                  <Typography className={styles.inputTitle}>{t('gender')}</Typography>
                  <Input
                    select
                    className={styles.textField}
                    variant={'outlined'}
                    id="gender"
                    name={'gender'}
                    value={userToEdit.gender}
                    label={''}
                    onChange={handleInput}
                  >
                    <MenuItem value={UserGender.Male}>{t('male')}</MenuItem>
                    <MenuItem value={UserGender.Female}>{t('female')}</MenuItem>
                  </Input>
                </Box>
              </Box>
              <Box mb={3} className={styles.textFieldBox}>
                <Box>
                  <Typography className={styles.inputTitle}>{t('role')}</Typography>
                  <Input
                    id={'role'}
                    label={''}
                    key={'role'}
                    name="role"
                    className={styles.textField}
                    value={t('externProfessional')}
                    type="text"
                    required={true}
                    variant={'outlined'}
                    size={'small'}
                    disabled={true}
                  />
                </Box>
                <Box>
                  <Typography className={styles.inputTitle}>{t('dob')}</Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'es'}>
                    <DatePicker
                      open={isDatePickerOpen}
                      onOpen={() => setIsDatePickerOpen(true)}
                      onClose={() => setIsDatePickerOpen(false)}
                      key={'finishDate'}
                      format={'DD/MM/YYYY'}
                      onChange={handleDateChange}
                      label={''}
                      className={styles.textField}
                      value={userToEdit.dob ? dayjs(userToEdit.dob) : null}
                      slotProps={{
                        textField: {
                          id: 'finishDate',
                          size: 'small',
                          InputProps: {
                            sx: {
                              '& fieldset': {
                                borderRadius: 32,
                                border: '1px solid #E8E7EC',
                                fontFamily: 'Montserrat',
                                fontSize: '13px',
                              },
                              '& .MuiInputBase-root': {
                                fontFamily: 'Montserrat',
                                fontSize: '13px',
                              },
                              '& .MuiOutlinedInput-root': {
                                border: 'none',
                                '&.Mui-focused fieldset': {
                                  borderColor: '#2E7161',
                                },
                              },
                            },
                            onClick: () => setIsDatePickerOpen(true),
                          },
                        },
                      }}
                      slots={{
                        openPickerIcon: CalendarIcon,
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              </Box>
              <Box
                mb={3}
                style={{ maxWidth: openMenuLeft ? '100%' : '77%' }}
                className={styles.headerContainer}
              >
                <Typography className={styles.headerTitle} sx={{ marginTop: 4 }}>
                  {t('changePass')}
                </Typography>
              </Box>
              <Box mb={3} className={styles.textFieldBox}>
                <Box>
                  <Typography className={styles.inputTitle}>{t('currentPassword')}</Typography>
                  <Input
                    id={'currentPass'}
                    label={''}
                    key={'currentPass'}
                    name="currentPass"
                    className={styles.textFieldPass}
                    type="password"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    variant={'outlined'}
                    size={'small'}
                  />
                </Box>
              </Box>
              <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                <Box mb={3} className={styles.textFieldBox}>
                  <Box>
                    <Typography className={styles.inputTitle}>{t('newPass')}</Typography>
                    <Input
                      id={'newPass'}
                      label={''}
                      key={'newPass'}
                      name="newPass"
                      className={styles.textFieldPass}
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      variant={'outlined'}
                      size={'small'}
                    />
                  </Box>
                </Box>
                <Box mb={3} className={styles.textFieldBox}>
                  <Box>
                    <Typography className={styles.inputTitle}>{t('repeatNewPass')}</Typography>
                    <Input
                      id={'repeatNewPass'}
                      label={''}
                      key={'repeatNewPass'}
                      name="repeatNewPass"
                      value={repeatPassword}
                      className={styles.textFieldPass}
                      type="password"
                      onChange={(e) => setRepeatPassword(e.target.value)}
                      variant={'outlined'}
                      size={'small'}
                    />
                  </Box>
                </Box>
                <Box className={styles.buttonContainer} display="flex">
                  <Button className={styles.buttonSubmit} type={'submit'} onClick={() => {}}>
                    {t('save')}
                  </Button>
                </Box>
              </Box>
              {message && (
                <Box mb={3}>
                  <Alert severity="success" key="message" id="message">
                    {t(message)}
                  </Alert>
                </Box>
              )}
              {errorMessage && (
                <Box>
                  <Alert severity="error" key="errorMessage" id="errorMessage">
                    {t(errorMessage)}
                  </Alert>
                </Box>
              )}
            </Box>
          </Box>
        </form>
      ) : (
        <form onSubmit={saveData}>
          <Box bgcolor="white" m="1rem">
            <Box className={styles.headerContainer}>
              <Header label={t('userData')} />
            </Box>
            <Box className={styles.textFieldBox}>
              <Skeleton className={styles.textField} height={70} />
              <Skeleton className={styles.textField} height={70} />
            </Box>
            <Box className={styles.textFieldBox}>
              <Skeleton className={styles.textField} height={70} />
              <Skeleton className={styles.textField} height={70} />
            </Box>
            <Box className={styles.textFieldBox}>
              <Skeleton className={styles.textField} height={70} />
            </Box>
            <Box style={{ borderBottom: '3px solid  #a9ba37' }}>
              <p className={styles.label}>{t('changePass')}</p>
            </Box>
            <Box className={styles.textFieldBox}>
              <Skeleton className={styles.textField} height={70} />
            </Box>
            <Box className={styles.textFieldBox}>
              <Skeleton className={styles.textField} height={70} />
            </Box>
            <Box className={styles.textFieldBox}>
              <Skeleton className={styles.textField} height={70} />
            </Box>
          </Box>
          <Box className={styles.buttonContainer} display="flex">
            <AppButton
              theme={ButtonTheme.NewPrimary}
              type={'submit'}
              label={t('save')}
              handler={() => {}}
            />
          </Box>
        </form>
      )}
    </Box>
  )
}
